import styled from '@emotion/styled';
import {Spacer} from '@nfq/react-grid';

import {FooterLink, H4, P} from 'UI/components/layout/Text';

import {useContactColumn} from './useContactColumn';

import type {FooterColumn} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the properties for the `ContactColumn` component.
 * This interface specifies the required footer column data containing contact information and an optional test identifier.
 * It ensures type safety when passing contact details such as phone numbers, email addresses, and physical addresses to the component.
 */
interface ComponentProps {
    /**
     * The footer column configuration object containing all contact information data.
     * This includes properties like phone numbers, email addresses, physical addresses, and other contact-related details that will be displayed in the contact section.
     */
    column: FooterColumn;
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable test automation.
     * It provides a consistent way to locate and interact with the contact column component in automated tests.
     *
     * @default 'ContactColumn'
     */
    testId?: string;
}

/**
 * The ContactColumn component renders a contact information section displaying phone, email, and address details.
 * It utilizes the useContactColumn hook to process and format contact data from a footer column configuration.
 * This component is designed to be part of a larger footer layout structure and provides consistent styling for contact information display.
 *
 * @param props        The component props.
 * @param props.column The footer column configuration containing contact information data.
 * @param props.testId Test identifier for component testing purposes.
 * @returns The rendered contact column component with formatted contact information.
 *
 * @example
 * ```tsx
 * <ContactColumn
 *     column={{
 *         phone: '+49 123 456789',
 *         mail: 'info@example.com',
 *         address: '123 Main St, City, Country'
 *     }}
 *     testId="contact-section"
 * />
 * ```
 */
const ContactColumn = ({column, testId = 'ContactColumn'}: ComponentProps) => {
    const {
        mailLabel,
        mailRel,
        mailTarget,
        mailToLink,
        phoneLabel,
        phoneLink,
        phoneRel,
        phoneTarget
    } = useContactColumn(column);

    return (
        <Wrapper data-cy={testId}>
            <Spacer y={6} />
            <H4>Kontakt</H4>
            <Spacer y={4} />
            <FooterLink href={phoneLink} rel={phoneRel} target={phoneTarget}>{phoneLabel}</FooterLink>
            <Spacer y={2} />
            <FooterLink href={mailToLink} rel={mailRel} target={mailTarget}>{mailLabel}</FooterLink>
            <Spacer y={2} />
            <P>{column.address}</P>
        </Wrapper>
    );
};

ContactColumn.displayName = 'ContactColumn';

export {ContactColumn};

const Wrapper = styled.div``;